import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FitMathDisplay from './FitMathDisplay';
import FuturisticMathCanvas from './FuturisticMathCanvas';
import { INFINITE_MATH_SLIDES } from './mathSlideData';
import './InfiniteMathCarousel.css';

const ROTATE_MS = 7000;

export default function InfiniteMathCarousel() {
    const [active, setActive] = useState(0);
    const total = INFINITE_MATH_SLIDES.length;

    const goTo = useCallback((index) => {
        setActive(((index % total) + total) % total);
    }, [total]);

    const next = useCallback(() => goTo(active + 1), [active, goTo]);
    const prev = useCallback(() => goTo(active - 1), [active, goTo]);

    useEffect(() => {
        const timer = window.setInterval(next, ROTATE_MS);
        return () => window.clearInterval(timer);
    }, [next]);

    return (
        <Box className="infinite-math-carousel" role="region" aria-label="Infinite series mathematics carousel">
            {INFINITE_MATH_SLIDES.map((slide, index) => (
                <Box
                    key={slide.id}
                    className={`math-slide-layer${index === active ? ' active' : ''}`}
                    aria-hidden={index !== active}
                >
                    {index === active && (
                        <FuturisticMathCanvas
                            variant={slide.animation}
                            accentColor={slide.accent}
                            active
                        />
                    )}
                    {index === active && (
                        <Box className="math-slide-content">
                            <Box className="math-slide-label">
                                <span
                                    className="math-slide-label-dot"
                                    style={{ backgroundColor: slide.accent, boxShadow: `0 0 10px ${slide.accent}` }}
                                />
                                {slide.label}
                            </Box>
                            <Box
                                className="math-slide-equation"
                                sx={{
                                    '--slide-accent': slide.accent,
                                    '--text-accent': slide.text_accent,
                                }}
                            >
                                <FitMathDisplay
                                    text={slide.latex}
                                    minScale={0.45}
                                    accentColor={slide.text_accent}
                                    glowColor={slide.accent}
                                />
                            </Box>
                            <Box className="math-slide-caption" component="p">
                                {slide.caption}
                            </Box>
                        </Box>
                    )}
                </Box>
            ))}

            <Box className="carousel-controls">
                <IconButton size="small" className="carousel-nav-btn" onClick={prev} aria-label="Previous series">
                    <ChevronLeftIcon fontSize="small" />
                </IconButton>
                {INFINITE_MATH_SLIDES.map((slide, index) => (
                    <button
                        key={slide.id}
                        type="button"
                        className={`carousel-dot${index === active ? ' active' : ''}`}
                        style={index === active ? { backgroundColor: slide.accent, color: slide.accent } : undefined}
                        onClick={() => goTo(index)}
                        aria-label={`Show ${slide.label}`}
                        aria-current={index === active}
                    />
                ))}
                <span className="carousel-counter">
                    {String(active + 1).padStart(2, '0')}/{String(total).padStart(2, '0')}
                </span>
                <IconButton size="small" className="carousel-nav-btn" onClick={next} aria-label="Next series">
                    <ChevronRightIcon fontSize="small" />
                </IconButton>
            </Box>
        </Box>
    );
}
